import moment from 'moment'

import { sortBy } from 'lib/array/arrayUtils'
import { SortableItemPresentation } from 'tripPlanner/models'
import { TripItem } from 'tripPlanner/types/tripItem'
import {
  getItemPresentations,
  getTimezoneAdjustedTime,
  getTripItemUTCEndTime,
  getTripItemUTCStartTime,
  isReturnFlightSearchBookmark,
} from 'tripPlanner/utils/index'

export type PresentationTripItem<T extends TripItem = TripItem> = {
  tripItem: T
  isMultiDay: boolean
} & SortableItemPresentation

/** Old way, before rank-based sorting */
export const generateFilteredTripItems = <T extends TripItem = TripItem>(
  tripItems: T[],
  selectedDate: moment.Moment,
): PresentationTripItem<T>[] => {
  return tripItems
    .filter((tripItem) => tripItem.isInItinerary)
    .flatMap((tripItem) => {
      const itemPresentations = getItemPresentations(
        selectedDate,
        tripItem,
        true,
      )
      const isMultiDay = itemPresentations.length === 1 // if there is only 1 presentation on this day, its single day
      return itemPresentations.map((itemPresentation) => ({
        tripItem,
        isMultiDay,
        ...itemPresentation,
      }))
    })
    .filter((pItem) => !!pItem.date)
}

export const sortItemsByTimeWithDefaults = (
  presentationItems: PresentationTripItem[],
): PresentationTripItem[] => {
  return sortBy(
    presentationItems,
    (item) => {
      const { tripItem, presentationType } = item

      // Use actual time on item if it exists
      let utcTime: moment.Moment | undefined
      if (presentationType === 'start') {
        utcTime = getTripItemUTCStartTime(tripItem)
      } else if (presentationType === 'end') {
        utcTime = getTripItemUTCEndTime(tripItem)
      }
      if (utcTime) {
        return utcTime.valueOf()
      }

      // For hotels, if there are no times, default to 3pm check-in and 10am checkout
      if (tripItem.type === 'HOTEL' && tripItem.startTimezone) {
        if (presentationType === 'start' && tripItem.startDate) {
          return getTimezoneAdjustedTime(
            tripItem.startDate,
            tripItem.startTimezone,
            15, // 3pm
            'hours',
          ).valueOf()
        } else if (presentationType === 'end' && tripItem.endDate) {
          return getTimezoneAdjustedTime(
            tripItem.endDate,
            tripItem.startTimezone, // it's a hotel so we only have one timezone
            10, // 10am
            'hours',
          ).valueOf()
        } else {
          return 0 // continuing hotel stay should come first
        }
      }

      // If no time, sort to the bottom
      return Number.MAX_SAFE_INTEGER
    },
    'asc',
  )
}

/**
 * Filters out transport end items that come immediately after the corresponding start item
 */
export const removeUnneededEndItems = (
  presentationItems: PresentationTripItem[],
): PresentationTripItem[] => {
  return presentationItems.filter((item, i) => {
    if (item.presentationType === 'end') {
      const prevItem = presentationItems[i - 1]
      return !(
        prevItem &&
        prevItem.presentationType === 'start' &&
        prevItem.tripItem.id === item.tripItem.id &&
        !isReturnFlightSearchBookmark(item.tripItem)
      )
    }
    return true
  })
}

interface RankedItem {
  summaryRankString: string
  itineraryRankString: string
}

/** For use with array sort method */
export function bySummaryRank(a: RankedItem, b: RankedItem): number {
  if (a.summaryRankString === b.summaryRankString) {
    return 0
  }
  return a.summaryRankString < b.summaryRankString ? -1 : 1
}

/** For use with array sort method */
export function byItineraryRank(a: RankedItem, b: RankedItem): number {
  if (a.itineraryRankString === b.itineraryRankString) {
    return 0
  }
  return a.itineraryRankString < b.itineraryRankString ? -1 : 1
}

export function itemPresentationsByItineraryRank(
  a: PresentationTripItem,
  b: PresentationTripItem,
) {
  return byItineraryRank(a.tripItem, b.tripItem)
}

export function itemPresentationsBySummaryRank(
  a: PresentationTripItem,
  b: PresentationTripItem,
) {
  return bySummaryRank(a.tripItem, b.tripItem)
}
